import {
  Box,
  VStack,
  Icon,
  Tooltip,
  useColorModeValue,
  Flex,
  Text,
  Button,
  Image,
  IconButton,
  Badge,
  HStack,
  Divider,
  Collapse,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  FiHome,
  FiPlay,
  FiBarChart2,
  FiSettings,
  FiUser,
  FiBook,
  FiMenu,
  FiChevronDown,
  FiChevronUp,
  FiMessageSquare,
  FiZap,
  FiGrid,
  FiAward,
  FiList,
  FiStar,
  FiHeart,
  FiCompass,
  FiFileText
} from 'react-icons/fi';
import { FaBars, FaTimes, FaGraduationCap, FaFire } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../assets/styles/colors';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/apiClient';
import LogoWithBackground from './LogoWithBackground';
import { keyframes } from '@emotion/react';
import logger from '../utils/logger';

// Define animations
const rotate = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const bounce = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(138, 75, 175, 0.5); }
  50% { box-shadow: 0 0 15px rgba(138, 75, 175, 0.8); }
`;

interface SidebarProps {
  closeSidebar?: () => void;
  toggleSidebar: () => void;
  showSidebar: boolean;
}

const Sidebar = ({ closeSidebar, toggleSidebar, showSidebar }: SidebarProps) => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Color mode values
  const bgColor = useColorModeValue('white', colors.primary[700]);
  const textColor = useColorModeValue('gray.800', 'white');
  const activeColor = useColorModeValue(colors.primary[600], colors.primary[300]);
  const hoverBgColor = useColorModeValue('gray.100', 'rgba(255, 255, 255, 0.1)');
  const borderColor = useColorModeValue('gray.200', colors.primary[600]);
  const activeItemBg = useColorModeValue('purple.50', 'rgba(138, 75, 175, 0.2)');
  const streakBadgeBg = useColorModeValue('orange.50', 'orange.900');
  const streakBadgeColor = useColorModeValue('orange.700', 'orange.200');

  // Get the current user's streak
  const { data: userStats, isLoading: statsLoading } = useQuery({
    queryKey: ['user-stats', user?.id],
    queryFn: async () => {
      try {
        if (!user) return null;

        const data = await apiClient.getUserStats(user.id);

        if (!data) return null;

        return data;
      } catch (error) {
        logger.error('Error fetching user stats:', error);
        return null;
      }
    },
    enabled: !!user,
  });

  // Get the current streak or 0 if not available
  const currentStreak = userStats?.current_streak || 0;

  // Define the navigation items with updated icons
  const navItems = [
    {
      path: '/dashboard',
      icon: FiGrid,
      label: t('common.dashboard'),
      badge: null
    },
    {
      path: '/play',
      icon: FiPlay,
      label: t('common.practice'),
      badge: null
    },
    {
      path: '/smart-practice',
      icon: FiMessageSquare,
      label: t('common.smartPractice'),
      badge: null
    },
    {
      path: '/smart-read',
      icon: FiFileText,
      label: t('smartRead.title'),
      badge: 'New'
    },
    {
      path: '/analytics',
      icon: FiBarChart2,
      label: t('common.analytics'),
      badge: null
    },
    {
      path: '/profile',
      icon: FiUser,
      label: t('common.profile'),
      badge: null
    },
    {
      path: '/settings',
      icon: FiSettings,
      label: t('common.settings'),
      badge: null
    }
  ];

  // Check if the current path matches the nav item
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <Box h="100%" bg={bgColor} overflow="auto" position="relative" data-sidebar={showSidebar ? "open" : "closed"}>
      {/* Header with logo and beta tag */}
      <Flex
        h="70px"
        alignItems="center"
        justifyContent="space-between"
        borderBottomWidth="1px"
        borderColor={borderColor}
        px={4}
        position="relative"
      >
        <HStack spacing={2}>
          <IconButton
            onClick={toggleSidebar}
            variant="ghost"
            aria-label={showSidebar ? t('common.closeNavigation') : t('common.openNavigation')}
            icon={showSidebar ? <FaTimes /> : <FaBars />}
            size="md"
            color={activeColor}
            _hover={{
              bg: hoverBgColor,
              animation: `${rotate} 0.5s ease-in-out`
            }}
            sx={{
              '&:active': {
                animation: `${bounce} 0.5s ease`
              }
            }}
            transition="all 0.3s ease"
          />

          <RouterLink to="/dashboard">
            <Box position="relative">
              <LogoWithBackground
                height="40px"
                objectFit="contain"
                transition="transform 0.3s ease"
                _hover={{ transform: 'scale(1.05)' }}
              />
              <Badge
                colorScheme="green"
                position="absolute"
                top="-2px"
                right="-5px"
                fontSize="0.6em"
                transform="rotate(20deg)"
              >
                {t('common.beta')}
              </Badge>
            </Box>
          </RouterLink>
        </HStack>
      </Flex>

      {/* User streak badge */}
      {currentStreak > 0 && (
        <Flex
          justify="center"
          mt={3}
          mb={2}
        >
          <HStack
            bg={streakBadgeBg}
            color={streakBadgeColor}
            px={3}
            py={2}
            borderRadius="full"
            boxShadow="sm"
            fontSize="sm"
            fontWeight="bold"
            animation={`${glow} 2s infinite`}
            _hover={{
              transform: "translateY(-3px)",
              boxShadow: "md"
            }}
            transition="all 0.3s ease"
          >
            <Icon as={FaFire} color="orange.400" />
            <Text>{currentStreak} {t('common.dayStreak')}</Text>
          </HStack>
        </Flex>
      )}

      {/* Navigation items */}
      <VStack spacing={2} align="stretch" mt={4} px={3} mb={6}>
        {navItems.map(({ path, icon, label, badge }) => (
          <Tooltip
            key={path}
            label={label}
            placement="right"
            hasArrow
            openDelay={500}
            display={{ base: 'none', md: 'block' }}
          >
            <Box>
              <Button
                as={RouterLink}
                to={path}
                justifyContent="flex-start"
                alignItems="center"
                w="full"
                h="48px"
                pl={4}
                pr={4}
                borderRadius="lg"
                bg={isActive(path) ? activeItemBg : 'transparent'}
                color={isActive(path) ? activeColor : textColor}
                fontWeight={isActive(path) ? 'bold' : 'medium'}
                leftIcon={
                  <Icon
                    as={icon}
                    boxSize={5}
                    color={isActive(path) ? activeColor : textColor}
                    transition="transform 0.2s ease"
                  />
                }
                position="relative"
                variant="ghost"
                _hover={{
                  bg: hoverBgColor,
                  transform: "translateX(5px)",
                  '& svg': {
                    transform: "scale(1.2)"
                  }
                }}
                _active={{
                  bg: activeItemBg,
                }}
                transition="all 0.2s ease-in-out"
                role="group"
                onClick={closeSidebar}
              >
                {label}

                {badge && (
                  <Badge
                    colorScheme="green"
                    position="absolute"
                    top="6px"
                    right="10px"
                    borderRadius="full"
                    px={2}
                    fontSize="xs"
                    animation={`${bounce} 2s infinite`}
                  >
                    {badge}
                  </Badge>
                )}

                {isActive(path) && (
                  <Box
                    position="absolute"
                    left={0}
                    top="50%"
                    transform="translateY(-50%)"
                    width="4px"
                    height="70%"
                    bg={activeColor}
                    borderRightRadius="md"
                  />
                )}
              </Button>
            </Box>
          </Tooltip>
        ))}
      </VStack>
    </Box>
  );
};

export default Sidebar;